/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { useState } from 'react'

export const useSelectBatchItems = (initialIds = []) => {
  const [selectedIds, setSelectedIds] = useState(new Set(initialIds))
  const [excludedIds, setExcludedIds] = useState(new Set())
  const [isSelectAllMode, setIsSelectAllMode] = useState(false)

  const isSelected = (id) => {
    if (isSelectAllMode) {
      return !excludedIds.has(id)
    } else {
      return selectedIds.has(id)
    }
  }

  const addId = (id) => {
    if (isSelectAllMode) {
      setExcludedIds(prev => {
        const next = new Set(prev)

        next.delete(id)

        return next
      })
    } else {
      setSelectedIds(prev => new Set([...prev, id]))
    }
  }

  const removeId = (id) => {
    if (isSelectAllMode) {
      setExcludedIds(prev => new Set([...prev, id]))
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  const toggleSelection = (id) => {
    const addRemoveIds = prev => {
      const next = new Set(prev)

      next.has(id)
        ? next.delete(id)
        : next.add(id)

      return next
    }

    isSelectAllMode
      ? setExcludedIds(addRemoveIds)
      : setSelectedIds(addRemoveIds)
  }

  const clearSelection = () => {
    if (isSelectAllMode) {
      setExcludedIds(new Set())
    } else {
      setSelectedIds(new Set())
    }
  }

  const toggleSelectAllMode = () => {
    if (!isSelectAllMode) {
      setExcludedIds(new Set())
      setIsSelectAllMode(true)
    } else {
      setSelectedIds(new Set())
      setIsSelectAllMode(false)
    }
  }

  const setSelectAllMode = (newMode) => {
    if (newMode !== isSelectAllMode) {
      toggleSelectAllMode()
    }
  }

  const getIdsArray = () => {
    if (isSelectAllMode) {
      return Array.from(excludedIds)
    } else {
      return Array.from(selectedIds)
    }
  }

  const getSelectionInfo = () => {
    return {
      isSelectAll: isSelectAllMode,
      ids: getIdsArray()
    }
  }

  return {
    addId,
    clearSelection,
    excludedIds,
    getIdsArray,
    getSelectionInfo,
    isSelectAllMode,
    isSelected,
    removeId,
    selectedIds,
    setSelectAllMode,
    toggleSelectAllMode,
    toggleSelection
  }
}
